import {
  FirestoreCollections,
  lifeAreaOtherID,
  UpdateGoal,
} from '@griply/types';

import { dateToFirestoreTimestamp } from '../converters/date-to-firestore-timestamp';
import { detachWrite } from '../detach-write';
import { FirestoreInstance } from '../types/firestore';
import { stripUndefined } from '../utils/strip-undefined';

export type UpdateGoalOptions<
  TDocRef = unknown,
  TCollectionRef = unknown,
  TFieldValue = unknown,
  TTimestamp = unknown,
  TDb = FirestoreInstance,
> = {
  goal: UpdateGoal;
  forceValues?: boolean;
  collection: (
    db: NoInfer<TDb>,
    path: string,
    ...rest: string[]
  ) => TCollectionRef;
  doc: (collectionRef: NoInfer<TCollectionRef>, id: string) => TDocRef;
  updateDoc: (
    ref: NoInfer<TDocRef>,
    data: Record<string, unknown>,
  ) => Promise<void> | void;
  db: TDb;
  serverTimestamp: () => TFieldValue;
  timestampFromDate: (date: Date) => TTimestamp;
  increment: (n: number) => TFieldValue;
  onWriteError: (error: unknown) => void;
};

const isNumber = (value: unknown): value is number => typeof value === 'number';

export const updateGoal = async <
  TDocRef,
  TCollectionRef,
  TFieldValue,
  TTimestamp,
  TDb = FirestoreInstance,
>(
  options: UpdateGoalOptions<
    TDocRef,
    TCollectionRef,
    TFieldValue,
    TTimestamp,
    TDb
  >,
): Promise<void> => {
  const {
    goal,
    forceValues,
    collection,
    doc,
    updateDoc,
    db,
    serverTimestamp,
    timestampFromDate,
    increment,
    onWriteError,
  } = options;

  if (!goal.id) {
    return;
  }

  const cleaned = stripUndefined(goal);

  const goalCollection = collection(db, FirestoreCollections.Goals);
  const docRef = doc(goalCollection, cleaned.id);
  const { taskCount, completedTaskCount, parentIds, ...rest } = cleaned;
  const data = dateToFirestoreTimestamp(
    { ...rest },
    timestampFromDate,
  ) as Record<string, unknown>;

  if (forceValues && isNumber(taskCount)) {
    data.taskCount = taskCount;
  }
  if (forceValues && isNumber(completedTaskCount)) {
    data.completedTaskCount = completedTaskCount;
  }
  if (!forceValues && taskCount) {
    data.taskCount = increment(taskCount);
  }
  if (!forceValues && completedTaskCount) {
    data.completedTaskCount = increment(completedTaskCount);
  }
  if (parentIds?.length) {
    data.parentIds = Array.from(new Set(parentIds));
    data.lifeAreaId = null;
  }
  if (!parentIds?.length && parentIds !== undefined) {
    data.parentIds = null;
  }
  if (data.lifeAreaId === lifeAreaOtherID) {
    data.lifeAreaId = null;
  }

  detachWrite(
    updateDoc(docRef, { ...data, updatedAt: serverTimestamp() }),
    onWriteError,
  );
};
